/**
 * The common experience-resolution ABI (#2032).
 *
 * The database owns which authored layer wins. A host may decide only whether
 * it can render the returned document; it must never rebuild this cascade.
 */

import { recordOrNull as object } from './documentShape.js'

export type ExperienceRung =
  | 'user'
  | 'tenant'
  | 'solution'
  | 'persona'
  | 'system'
  | 'unreconciled'

export type ExperienceResolutionState = 'resolved' | 'absent' | 'refused'

export interface ExperienceResolutionSource {
  logicalKey: string
  id: string | null
  scope: string | null
  rung: ExperienceRung
  revision: number
  host: string | null
  persona: string | null
  solutionKey: string | null
  ownerId: string | null
  document: unknown
  documentState: 'absent' | 'empty' | 'present'
  editable: boolean
}

export interface ExperienceResolutionRung {
  rung: ExperienceRung
  count: number
  logicalKeys: string[]
}

export interface ExperienceResolutionConflict {
  rung: ExperienceRung
  logicalKeys: string[]
  reason: string
}

/** `core.resolve_experience`'s closed v1 envelope. */
export interface ResolvedExperienceEnvelope {
  contractVersion: 1
  entity: string
  artifact: string
  host: string | null
  assignedPersona: string | null
  effectivePersona: string | null
  personaSource: 'assigned' | 'admin_preview' | 'none'
  state: ExperienceResolutionState
  effective: ExperienceResolutionSource | null
  inherited: ExperienceResolutionSource | null
  availableRungs: ExperienceResolutionRung[]
  conflicts: ExperienceResolutionConflict[]
  issues: string[]
}

const RUNGS = new Set<ExperienceRung>([
  'user', 'tenant', 'solution', 'persona', 'system', 'unreconciled',
])

const stringOrNull = (value: unknown): string | null | undefined =>
  value === null ? null : typeof value === 'string' ? value : undefined

const strings = (value: unknown): string[] | null =>
  Array.isArray(value) && value.every((item) => typeof item === 'string')
    ? value as string[]
    : null

const source = (value: unknown): ExperienceResolutionSource | null | undefined => {
  if (value === null) return null
  const row = object(value)
  if (!row) return undefined
  const rung = row.rung
  const logicalKey = row.logicalKey
  const revision = row.revision
  const documentState = row.documentState
  const hasDocument = Object.prototype.hasOwnProperty.call(row, 'document')
  const document = row.document
  const actualDocumentState: ExperienceResolutionSource['documentState'] = document === null
    ? 'absent'
    : Array.isArray(document)
      ? document.length === 0 ? 'empty' : 'present'
      : object(document)
        ? Object.values(document as Record<string, unknown>).every(
            (item) => Array.isArray(item) && item.length === 0,
          ) ? 'empty' : 'present'
        : 'present'
  if (!RUNGS.has(rung as ExperienceRung)
      || typeof logicalKey !== 'string' || logicalKey.length === 0
      || typeof revision !== 'number' || !Number.isInteger(revision) || revision < 1
      || !['absent', 'empty', 'present'].includes(String(documentState))
      || !hasDocument || documentState !== actualDocumentState
      || typeof row.editable !== 'boolean') return undefined
  const id = stringOrNull(row.id)
  const scope = stringOrNull(row.scope)
  const host = stringOrNull(row.host)
  const persona = stringOrNull(row.persona)
  const solutionKey = stringOrNull(row.solutionKey)
  const ownerId = stringOrNull(row.ownerId)
  if ([id, scope, host, persona, solutionKey, ownerId].some((item) => item === undefined)) return undefined
  return {
    logicalKey,
    id: id as string | null,
    scope: scope as string | null,
    rung: rung as ExperienceRung,
    revision,
    host: host as string | null,
    persona: persona as string | null,
    solutionKey: solutionKey as string | null,
    ownerId: ownerId as string | null,
    document,
    documentState: documentState as ExperienceResolutionSource['documentState'],
    editable: row.editable,
  }
}

/**
 * Validate the transport boundary without throwing a page away.
 *
 * Unknown contract versions and malformed rows become a named, document-less
 * refusal. That is deliberately different from `absent`: an absent definition
 * may use the host's built-in surface; an unreadable server answer may not be
 * mistaken for "nothing was configured".
 */
export function parseResolvedExperience(value: unknown): ResolvedExperienceEnvelope {
  const row = object(value)
  const entity = typeof row?.entity === 'string' ? row.entity : ''
  const artifact = typeof row?.artifact === 'string' ? row.artifact : ''
  const host = stringOrNull(row?.host)
  const assignedPersona = stringOrNull(row?.assignedPersona)
  const effectivePersona = stringOrNull(row?.effectivePersona)
  const issues = strings(row?.issues)
  const effective = source(row?.effective)
  const inherited = source(row?.inherited)
  const personaSource = row?.personaSource
  const state = row?.state
  const available = Array.isArray(row?.availableRungs) ? row.availableRungs : null
  const conflicts = Array.isArray(row?.conflicts) ? row.conflicts : null

  const availableRungs: ExperienceResolutionRung[] | null = available?.map((item) => {
    const candidate = object(item)
    const keys = strings(candidate?.logicalKeys)
    if (!candidate || !RUNGS.has(candidate.rung as ExperienceRung)
        || typeof candidate.count !== 'number' || !Number.isInteger(candidate.count)
        || candidate.count < 1 || !keys) return null
    return { rung: candidate.rung as ExperienceRung, count: candidate.count, logicalKeys: keys }
  }).filter((item): item is ExperienceResolutionRung => item !== null) ?? null

  const parsedConflicts: ExperienceResolutionConflict[] | null = conflicts?.map((item) => {
    const candidate = object(item)
    const keys = strings(candidate?.logicalKeys)
    if (!candidate || !RUNGS.has(candidate.rung as ExperienceRung)
        || !keys || typeof candidate.reason !== 'string') return null
    return { rung: candidate.rung as ExperienceRung, logicalKeys: keys, reason: candidate.reason }
  }).filter((item): item is ExperienceResolutionConflict => item !== null) ?? null

  const valid = row?.contractVersion === 1
    && entity.length > 0 && artifact.length > 0
    && host !== undefined && assignedPersona !== undefined && effectivePersona !== undefined
    && ['assigned', 'admin_preview', 'none'].includes(String(personaSource))
    && ['resolved', 'absent', 'refused'].includes(String(state))
    && issues !== null && effective !== undefined && inherited !== undefined
    && availableRungs !== null && availableRungs.length === available?.length
    && parsedConflicts !== null && parsedConflicts.length === conflicts?.length
    && (state !== 'resolved' || effective !== null)
    && (state === 'resolved' || effective === null)

  if (!valid) {
    return {
      contractVersion: 1,
      entity,
      artifact,
      host: host ?? null,
      assignedPersona: assignedPersona ?? null,
      effectivePersona: effectivePersona ?? null,
      personaSource: 'none',
      state: 'refused',
      effective: null,
      inherited: null,
      availableRungs: [],
      conflicts: [],
      issues: ['the experience resolver returned an unsupported or malformed contract'],
    }
  }

  return {
    contractVersion: 1,
    entity,
    artifact,
    host: host as string | null,
    assignedPersona: assignedPersona as string | null,
    effectivePersona: effectivePersona as string | null,
    personaSource: personaSource as ResolvedExperienceEnvelope['personaSource'],
    state: state as ExperienceResolutionState,
    effective: effective as ExperienceResolutionSource | null,
    inherited: inherited as ExperienceResolutionSource | null,
    availableRungs: availableRungs as ExperienceResolutionRung[],
    conflicts: parsedConflicts as ExperienceResolutionConflict[],
    issues: issues as string[],
  }
}
